import React from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { ChevronDown, Menu as MenuIcon, PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { cn } from 'lib/utils';
import {
  ADMIN_NAV_SECTIONS,
  type AdminNavItem,
  resolveAdminContentTool,
} from './adminNavigation';

export const ADMIN_SIDEBAR_COLLAPSED_STORAGE_KEY = 'wf.admin.sidebarCollapsed';

const CONTENT_PATH = '/admin/content';

const readStoredCollapsed = () => {
  try {
    return window.localStorage.getItem(ADMIN_SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const storeCollapsed = (collapsed: boolean) => {
  try {
    window.localStorage.setItem(ADMIN_SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed));
  } catch {
    // Ignore unavailable storage; the toggle still works for this page view.
  }
};

// matchPath tolerates a trailing slash, so `/admin/content/?tab=...` behaves like `/admin/content?tab=...`.
const isContentPath = (pathname: string) => matchPath({ path: CONTENT_PATH, end: true }, pathname) !== null;

const isItemActive = (item: AdminNavItem, pathname: string, search: string) => {
  if (item.contentTool) {
    return isContentPath(pathname)
      && resolveAdminContentTool(new URLSearchParams(search).get('tab')) === item.contentTool;
  }
  return matchPath({ path: item.path, end: item.path === '/admin' }, pathname) !== null;
};

// Switching Content tools keeps the other tools' query params, matching the former tab bar.
const getItemHref = (item: AdminNavItem, pathname: string, search: string) => {
  if (!item.contentTool) {
    return item.path;
  }
  const params = new URLSearchParams(isContentPath(pathname) ? search : '');
  params.set('tab', item.contentTool);
  return `${CONTENT_PATH}?${params.toString()}`;
};

type AdminNavListProps = {
  collapsed: boolean;
  onNavigate?: () => void;
};

const AdminNavList: React.FC<AdminNavListProps> = ({ collapsed, onNavigate }) => {
  const { pathname, search } = useLocation();

  return (
    <nav aria-label="Admin pages" className="flex flex-col">
      {ADMIN_NAV_SECTIONS.map((section) => (
        <div key={section.id} className={cn(section.label && 'mt-2')}>
          {section.label ? (
            collapsed ? (
              <div className="mx-3 mb-2 border-t border-foundation-base-darkbase-dark-500/10" aria-hidden="true" />
            ) : (
              <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {section.label}
              </p>
            )
          ) : null}
          <ul className="flex flex-col">
            {section.items.map((item) => {
              const active = isItemActive(item, pathname, search);
              const ItemIcon = item.icon;
              const link = (
                <Link
                  to={getItemHref(item, pathname, search)}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  data-testid={`admin-nav-${item.id}`}
                  className={cn(
                    'flex items-center gap-3 rounded-xl py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    collapsed ? 'justify-center px-2' : 'px-3',
                    active
                      ? 'bg-foundation-orange-50 font-semibold text-foundation-orange-500'
                      : 'text-foreground hover:bg-white/60',
                  )}
                >
                  <ItemIcon
                    size={18}
                    aria-hidden="true"
                    className={cn('shrink-0', active ? 'text-foundation-orange-500' : 'text-muted-foreground')}
                  />
                  {/* Collapsed links keep their name for assistive tech; the tooltip only adds a visual label. */}
                  <span className={collapsed ? 'sr-only' : 'truncate'}>{item.label}</span>
                </Link>
              );

              return (
                <li key={item.id}>
                  {item.separatorBefore && !collapsed ? (
                    <div className="mx-3 my-0.5 border-t border-foundation-base-darkbase-dark-500/10" aria-hidden="true" />
                  ) : null}
                  {collapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>{link}</TooltipTrigger>
                      <TooltipContent side="right">{item.label}</TooltipContent>
                    </Tooltip>
                  ) : (
                    link
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
};

export const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = React.useState(readStoredCollapsed);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const mobileToggleRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      storeCollapsed(!current);
      return !current;
    });
  };

  // Closing the menu unmounts the focused link, so hand focus back to the menu button.
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    mobileToggleRef.current?.focus();
  };

  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-2" data-testid="admin-layout">
      <div className="lg:hidden">
        <button
          ref={mobileToggleRef}
          type="button"
          onClick={() => setMobileMenuOpen((open) => !open)}
          aria-expanded={mobileMenuOpen}
          aria-controls="admin-mobile-nav"
          className="flex w-full items-center gap-3 rounded-2xl bg-white/40 px-4 py-3 text-sm font-semibold text-foreground"
          data-testid="admin-mobile-nav-toggle"
        >
          <MenuIcon size={18} aria-hidden="true" />
          <span className="flex-1 text-left">Admin pages</span>
          <ChevronDown
            size={18}
            aria-hidden="true"
            className={cn('transition-transform', mobileMenuOpen && 'rotate-180')}
          />
        </button>
        <div
          id="admin-mobile-nav"
          hidden={!mobileMenuOpen}
          className="mt-2 rounded-3xl bg-white/40 p-2"
          data-testid="admin-mobile-nav"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              closeMobileMenu();
            }
          }}
        >
          {/* Rendered only while open so the page has a single set of admin nav test IDs. */}
          {mobileMenuOpen ? <AdminNavList collapsed={false} onNavigate={closeMobileMenu} /> : null}
        </div>
      </div>

      <aside
        className={cn(
          'hidden shrink-0 rounded-3xl bg-white/40 p-2 transition-[width] duration-200 lg:sticky lg:top-4 lg:block lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto',
          collapsed ? 'w-[60px]' : 'w-60',
        )}
        data-testid="admin-sidebar"
        data-collapsed={collapsed ? 'true' : 'false'}
      >
        <div className={cn('mb-1 flex items-center', collapsed ? 'justify-center' : 'justify-between pl-3')}>
          {collapsed ? null : <span className="text-sm font-semibold text-foreground">Admin</span>}
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand admin sidebar' : 'Collapse admin sidebar'}
            aria-expanded={!collapsed}
            aria-controls="admin-sidebar-nav"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="rounded-xl p-2 text-muted-foreground transition-colors hover:bg-white/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="admin-sidebar-toggle"
          >
            <ToggleIcon size={18} aria-hidden="true" />
          </button>
        </div>
        <div id="admin-sidebar-nav">
          <TooltipProvider delayDuration={150}>
            <AdminNavList collapsed={collapsed} />
          </TooltipProvider>
        </div>
      </aside>

      <div className="min-w-0 flex-1" data-testid="admin-layout-content">
        {children}
      </div>
    </div>
  );
};
